import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import {
  LayoutDashboard,
  Search,
  Calculator,
  ClipboardCheck,
  Kanban,
  FileText,
  Users,
  LogOut,
  ChevronRight,
  CalendarDays,
  Bell,
  Handshake,
  Wrench,
  BookOpen,
  ClipboardList,
  Settings,
  HelpCircle,
  X,
  Network,
  Globe,
  Star,
  Sliders,
  UserCheck,
  Banknote
} from 'lucide-react';
import { Button } from './ui/button';

const NAV_MAP = {
  '/app': { icon: LayoutDashboard, label: 'Tableau de bord' },
  '/app/recherche': { icon: Search, label: 'Recherche Biens' },
  '/app/etude': { icon: Calculator, label: 'Etude Rentabilite' },
  '/app/visites': { icon: ClipboardCheck, label: 'Visites Techniques' },
  '/app/offres': { icon: Handshake, label: "Offres d'achat" },
  '/app/pipeline': { icon: Kanban, label: 'Pipeline Projets' },
  '/app/travaux': { icon: Wrench, label: 'Suivi Travaux' },
  '/app/bordereau': { icon: BookOpen, label: 'Bordereau de prix' },
  '/app/bons-commande': { icon: ClipboardList, label: 'Bons de commande' },
  '/app/dossier-financement': { icon: Banknote, label: 'Dossier de financement' },
  '/app/calendrier': { icon: CalendarDays, label: 'Calendrier' },
  '/app/reseau': { icon: Network, label: 'Réseau' },
  '/app/documents': { icon: FileText, label: 'Documents' },
};

const DEFAULT_ORDER = [
  '/app', '/app/recherche', '/app/etude', '/app/visites', '/app/offres',
  '/app/pipeline', '/app/travaux', '/app/bordereau', '/app/bons-commande', '/app/dossier-financement', '/app/calendrier', '/app/reseau', '/app/documents'
];

const adminItems = [
  { path: '/app/admin', icon: Users, label: 'Administration' },
  { path: '/app/admin/subscribers', icon: UserCheck, label: 'Abonnés' },
  { path: '/app/admin/settings', icon: Sliders, label: 'Paramètres système' },
  { path: '/app/admin/landing', icon: Globe, label: 'CMS Landing' },
  { path: '/app/admin/reviews', icon: Star, label: 'Modération avis' },
];

const universalItems = [
  { path: '/app/reviews', icon: Star, label: 'Laisser un avis' },
  { path: '/app/parametres', icon: Settings, label: 'Personnalisation' },
  { path: '/app/aide', icon: HelpCircle, label: 'Aide' },
];

export const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const { user, logout, isAdmin } = useAuth();
  const { prefs } = usePreferences();
  const navigate = useNavigate();

  // Build ordered nav items from preferences (fallback to default)
  // Artisan : vue épurée, UNIQUEMENT les BDC
  const isArtisan = user?.role === 'artisan';
  let orderedPaths;
  if (isArtisan) {
    orderedPaths = ['/app/bons-commande'];
  } else {
    const savedOrder = prefs?.sidebar_order && prefs.sidebar_order.length > 0 ? prefs.sidebar_order : [];
    const missing = DEFAULT_ORDER.filter(p => !savedOrder.includes(p));
    orderedPaths = savedOrder.length > 0 ? [...savedOrder, ...missing] : DEFAULT_ORDER;
  }
  const navItems = orderedPaths
    .map(p => ({ path: p, ...NAV_MAP[p] }))
    .filter(it => !!it.icon);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className={`w-64 fixed inset-y-0 left-0 h-screen border-r border-zinc-800 bg-[#0A0A0B] flex flex-col z-50 transition-transform duration-200 ease-out ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_asset-manager-359/artifacts/iy2gkryy_Home%20MDB%202A.png"
            alt="Home MDB 2A"
            className="h-12 w-12 object-contain rounded"
          />
          <div>
            <h1 className="text-base font-bold text-white font-['Outfit'] leading-tight">Home MDB 2A</h1>
            <p className="text-[10px] text-zinc-500">Marchand de Biens</p>
          </div>
        </div>
        <button
          className="md:hidden p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400"
          onClick={onMobileClose}
          aria-label="Fermer le menu"
          data-testid="mobile-sidebar-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              onClick={onMobileClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                  isActive
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`
              }
              style={({ isActive }) => isActive ? { color: '#fff' } : undefined}
            >
              {({ isActive }) => (
                <>
                  <item.icon className="w-5 h-5" style={isActive ? { color: 'var(--accent-color, #10B981)' } : undefined} />
                  <span className="flex-1">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4" style={{ color: 'var(--accent-color, #10B981)' }} />}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {isAdmin && (
          <>
            <div className="mt-6 mb-2 px-3">
              <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Admin</span>
            </div>
            <div className="space-y-1">
              {adminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-zinc-800 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="w-5 h-5" style={isActive ? { color: 'var(--accent-color, #10B981)' } : undefined} />
                      <span className="flex-1">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4" style={{ color: 'var(--accent-color, #10B981)' }} />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </>
        )}

        {/* Items universels (tous les rôles sauf artisan) */}
        {!isArtisan && (
          <>
            <div className="mt-6 mb-2 px-3">
              <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Mon compte</span>
            </div>
            <div className="space-y-1">
              {universalItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-zinc-800 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="w-5 h-5" style={isActive ? { color: 'var(--accent-color, #10B981)' } : undefined} />
                      <span className="flex-1">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4" style={{ color: 'var(--accent-color, #10B981)' }} />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-zinc-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.role === 'admin' ? 'Administrateur' : (user?.role === 'artisan' ? 'Artisan' : 'Collaborateur')}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 text-zinc-400 border-zinc-700 hover:text-white hover:bg-zinc-800"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};
